import React, { useEffect, useDebugValue } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  getUnreadInboxNotificationCount,
  isUnreadNotification,
} from '../utils/NotificationsUtils';
import { useGetInboxNotificationsQuery } from '../services/api/inboxApi';
import { PortalConfigContext } from '../context';
import { RootState } from '../store';

/**
 * A custom React hook that calculates the notification count for internal or client users
 * and updates the page title with the count and title.
 *
 * This hook uses data from the Redux store to determine notification counts and
 * uses the `useEffect` hook to update the document title based on the notification counts.
 *
 * @returns {void}
 */
export const useUserNotification = () => {
  // Fetch inbox notifications using the useGetInboxNotificationsQuery hook
  const { data: inboxNotifications = [] } = useGetInboxNotificationsQuery();
  const portalConfig = React.useContext(PortalConfigContext);

  // Extract relevant state properties using useSelector from the Redux store
  const {
    isClient,
    formCompletedNotifications,
    formRequestNotifications,
    totalUnreadMessages,
    newInvoiceNotifications,
  } = useSelector(
    (state: RootState) => ({
      formCompletedNotifications:
        state.notifications.formCompletedNotifications || [],
      formRequestNotifications:
        state.notifications.formRequestNotifications || [],
      totalUnreadMessages: state.messages.totalUnreadMessages || 0,
      newInvoiceNotifications:
        state.notifications.newInvoiceNotifications || [],
      isClient: state.user.isClient,
    }),
    shallowEqual,
  );

  // Determine the appropriate form notifications based on user type
  const formsNotification = isClient
    ? formRequestNotifications
    : formCompletedNotifications;

  // Filter unread form notifications
  const unreadFormNotifications = formsNotification.filter(notif =>
    isUnreadNotification(notif),
  );

  // Calculate the count of unread inbox notifications
  const unreadInboxNotificationCount = React.useMemo(
    () => getUnreadInboxNotificationCount(inboxNotifications),
    [inboxNotifications],
  );

  /**
   * Calculates the notification count for internal users by summing up
   * the total unread messages, unread inbox notifications, and form notifications.
   *
   * @returns {number} The total notification count for internal users.
   */
  const getInternalUserNotificationCount = () => {
    const formNotifications = unreadFormNotifications.length;
    return (
      totalUnreadMessages + unreadInboxNotificationCount + formNotifications
    );
  };

  /**
   * Calculates the notification count for client users by summing up
   * the total unread messages, unread inbox notifications, billing notifications, and form notifications.
   *
   * @returns {number} The total notification count for client users.
   */
  const getClientUserNotificationCount = () => {
    const formNotifications = unreadFormNotifications.length;
    const billingNotifications = newInvoiceNotifications.length;
    return (
      totalUnreadMessages +
      unreadInboxNotificationCount +
      billingNotifications +
      formNotifications
    );
  };

  // Calculate the total notification count for client and internal users
  const totalClientNotifications = getClientUserNotificationCount();
  const totalInternalNotifications = getInternalUserNotificationCount();

  useDebugValue({
    totalClientNotifications,
    totalInternalNotifications,
    totalUnreadMessages,
  });

  // Update the document title based on the user type and notification counts
  useEffect(() => {
    const notificationCount = isClient
      ? totalClientNotifications
      : totalInternalNotifications;
    const pageTitle = isClient
      ? portalConfig.name
      : portalConfig.metadata.titleOverride;

    document.title = `${
      notificationCount > 0 ? `(${notificationCount})` : ''
    } ${pageTitle}`;
  }, [totalClientNotifications, totalInternalNotifications, portalConfig]);
};
